/**
 * Trash-guard helpers.
 *
 * The trash endpoints reject with HTTP 409 `{detail: {code: 'trash_guard',
 * guarded: [...]}}` when the selection contains order-related or
 * currently-Important emails and no confirm flag was sent (owner directive
 * 2026-08-25: non-spam emails must never be hidden silently — the dialog
 * must list the specific emails, no guess-and-check).
 *
 * These are pure functions so they can be unit-tested directly, per the
 * repo's extract-logic-from-components convention.
 */

export type TrashGuardReason = 'order' | 'important';

export interface GuardedTrashEmail {
  id: string;
  subject: string;
  from_address: string | null;
  received_at: string | null;
  reason: TrashGuardReason;
}

/** Human label for a guard reason, used as the row badge text. */
export const GUARD_REASON_LABEL: Record<TrashGuardReason, string> = {
  order: 'Order',
  important: 'Important',
};

interface AxiosishError {
  response?: {
    status?: number;
    data?: {
      detail?: unknown;
    };
  };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/**
 * Extract the guarded-email list from a failed trash request.
 *
 * Returns the list when the error is a 409 trash_guard rejection, or
 * `null` for every other error (caller falls back to its normal error
 * handling). Malformed rows are dropped; an empty result maps to null.
 */
export function parseTrashGuardError(err: unknown): GuardedTrashEmail[] | null {
  const response = (err as AxiosishError | null)?.response;
  if (!response || response.status !== 409) return null;
  const detail = response.data?.detail;
  if (!isRecord(detail) || detail.code !== 'trash_guard') return null;
  const rawGuarded = detail.guarded;
  if (!Array.isArray(rawGuarded)) return null;

  const guarded: GuardedTrashEmail[] = [];
  for (const g of rawGuarded) {
    if (!isRecord(g) || typeof g.id !== 'string') continue;
    guarded.push({
      id: g.id,
      subject: typeof g.subject === 'string' && g.subject ? g.subject : '(No Subject)',
      from_address: typeof g.from_address === 'string' ? g.from_address : null,
      received_at: typeof g.received_at === 'string' ? g.received_at : null,
      reason: g.reason === 'important' ? 'important' : 'order',
    });
  }
  return guarded.length > 0 ? guarded : null;
}

/**
 * Split a trash selection into the server-guarded ids and the rest.
 *
 * Powers the dialog's "Skip these, trash the rest" action: re-request
 * with `unguardedIds` only. Selection order is preserved.
 */
export function partitionGuarded(
  selectedIds: readonly string[],
  guarded: readonly GuardedTrashEmail[],
): { guardedIds: string[]; unguardedIds: string[] } {
  const guardedSet = new Set(guarded.map((g) => g.id));
  return {
    guardedIds: selectedIds.filter((id) => guardedSet.has(id)),
    unguardedIds: selectedIds.filter((id) => !guardedSet.has(id)),
  };
}
